import type { RequestOptions } from '@@/plugin-request/request';
import {
  getTenantContextGeneration,
  readCurrentParkUuid,
  readCurrentTenantUuid,
} from '@/adapters/tenantStorage';
import { getSessionSnapshot } from '@/state/sessionSnapshot';

export const REQUEST_CONTEXT_FIELD = '__ownBackendRequestContext';

export class RequestContextAbortedError extends Error {
  constructor() {
    super('请求上下文已失效');
    this.name = 'RequestContextAbortedError';
  }
}

type RequestSessionUser = {
  tenantUuid?: string;
  organizationUuid?: string;
  lastLoginOrganizationUuid?: string;
};

export type OwnBackendRequestContext = {
  tenantUuid?: string;
  organizationUuid?: string;
  parkUuid?: string;
  requirePark: boolean;
  generation: number;
};

export type RequestOptionsWithContext = RequestOptions & {
  [REQUEST_CONTEXT_FIELD]?: OwnBackendRequestContext;
};

const readSessionUser = (): RequestSessionUser | null | undefined =>
  getSessionSnapshot().currentUser as RequestSessionUser | null | undefined;

export const getRequestTenantUuid = () => {
  return readSessionUser()?.tenantUuid || readCurrentTenantUuid() || undefined;
};

export const getRequestOrganizationUuid = () => {
  const sessionUser = readSessionUser();
  return (
    sessionUser?.organizationUuid ||
    sessionUser?.lastLoginOrganizationUuid ||
    undefined
  );
};

export const captureOwnBackendRequestContext = ({
  requirePark,
}: {
  requirePark: boolean;
}): OwnBackendRequestContext => {
  const parkUuid = requirePark ? readCurrentParkUuid() || undefined : undefined;
  return {
    tenantUuid: getRequestTenantUuid(),
    organizationUuid: getRequestOrganizationUuid(),
    parkUuid,
    requirePark,
    generation: getTenantContextGeneration(),
  };
};

export const attachRequestContext = (
  options: RequestOptions | undefined,
  snapshot: OwnBackendRequestContext,
): RequestOptionsWithContext => ({
  ...(options || {}),
  [REQUEST_CONTEXT_FIELD]: snapshot,
});

export const readAttachedRequestContext = (
  options?: RequestOptions,
): OwnBackendRequestContext | undefined =>
  (options as RequestOptionsWithContext | undefined)?.[REQUEST_CONTEXT_FIELD];

export const readRequestContextFromError = (
  error: {
    config?: RequestOptions;
    request?: unknown;
  },
  opts?: RequestOptions,
) =>
  readAttachedRequestContext(opts) ||
  readAttachedRequestContext(error.config) ||
  readAttachedRequestContext(error.request as RequestOptions | undefined);

export const resolveRequestContext = (
  options: RequestOptions | undefined,
  fallback: { requirePark: boolean },
) =>
  readAttachedRequestContext(options) ||
  captureOwnBackendRequestContext(fallback);

export const isRequestContextStale = (snapshot: OwnBackendRequestContext) =>
  snapshot.generation !== getTenantContextGeneration();

export const assertRequestContextStillValid = (
  snapshot: OwnBackendRequestContext,
) => {
  if (isRequestContextStale(snapshot)) {
    throw new RequestContextAbortedError();
  }
};

export const applyRequestContextHeaders = (
  headers: Record<string, string>,
  snapshot: OwnBackendRequestContext,
) => {
  if (snapshot.tenantUuid) {
    headers['x-tenant-id'] = snapshot.tenantUuid;
  }
  if (snapshot.organizationUuid) {
    headers['x-organization-id'] = snapshot.organizationUuid;
  }
  if (snapshot.requirePark && snapshot.parkUuid) {
    headers['x-park-uuid'] = snapshot.parkUuid;
  }
  return headers;
};

export const isRequestContextAbortedError = (error: unknown) =>
  error instanceof RequestContextAbortedError ||
  (typeof error === 'object' &&
    error !== null &&
    (error as { name?: string }).name === 'RequestContextAbortedError');
